import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import styles from './BooleanRadio.module.css';
import { issueMessage } from './issueMessage';

export interface BooleanRadioProps {
  field: FieldDefinition;
  value: boolean | undefined;
  issues: readonly ValidationIssue[];
  onChange: (next: boolean) => void;
  /**
   * Whether the user has interacted with the field. When `false`,
   * issues are suppressed. Defaults to `true` for backward
   * compatibility.
   */
  touched?: boolean;
  /** Fires when focus leaves the radio group. */
  onBlur?: () => void;
}

/**
 * Catalog-driven boolean primitive. Renders a Yes/No radio pair —
 * USCIS booleans are always asked as a question, never as a single
 * checkbox. Per Phase 2 plan: every catalog `boolean` field is a
 * question.
 *
 * Visual modernization to a segmented
 * pill control. Each option is a `<label>` wrapping a visually-hidden
 * `<input type="radio">` and a visible `<span>` for the text. The
 * pill takes the click anywhere within itself (the label's `htmlFor`
 * triggers the input regardless of where inside the label the click
 * lands), which eliminates the dual click-target ambiguity that
 * caused multi-click regressions previously. Selected state is driven
 * by `:has(input:checked)` in the module CSS — no React-controlled
 * data-attribute needed.
 *
 * Accessibility notes:
 *   - The input is visually hidden via the `.sr_only` recipe (clip +
 *     1px size + overflow hidden). It stays focusable and announceable
 *     by AT — only the visual chrome moves to the wrapping label.
 *   - The wrapping `<label>` carries `:focus-within` so the focus ring
 *     follows the focused input visually.
 *   - The fieldset+legend semantics are unchanged from the prior
 *     implementation, so `aria-required` and the existing screen-reader
 *     contract still hold (Phase 7h test asserts this; preserved).
 */
export function BooleanRadio({
  field,
  value,
  issues,
  onChange,
  touched = true,
  onBlur,
}: BooleanRadioProps): JSX.Element {
  const groupId = `field-${field.id.replace(/\./g, '-')}`;
  const yesId = `${groupId}-yes`;
  const noId = `${groupId}-no`;
  const errorListId = `${groupId}-errors`;
  const showIssues = touched && issues.length > 0;
  const radioGroupClassName = showIssues
    ? `${styles['field__radio-group']} ${styles['field__radio-group--invalid']}`
    : styles['field__radio-group'];
  const name = groupId;

  return (
    <fieldset
      className={styles.field}
      data-field-id={field.id}
      onBlur={onBlur}
    >
      <legend className={styles.field__legend}>
        {field.label}
        {field.required === true ? (
          <span className={styles['field__required-mark']}>*</span>
        ) : null}
      </legend>

      {/* ARIA attributes (required, invalid, describedby) belong on
          the role="radiogroup" wrapper, NOT the <fieldset> — <fieldset>
          is not a valid host for those attributes (axe:
          aria-allowed-attr). */}
      <div
        className={radioGroupClassName}
        role="radiogroup"
        aria-labelledby={undefined}
        aria-required={field.required === true || undefined}
        aria-invalid={showIssues || undefined}
        aria-describedby={showIssues ? errorListId : undefined}
      >
        <label
          htmlFor={yesId}
          className={styles['field__radio-option']}
          data-checked={value === true ? 'true' : 'false'}
        >
          <input
            id={yesId}
            type="radio"
            name={name}
            value="yes"
            checked={value === true}
            onChange={() => onChange(true)}
            className={styles['field__radio-input']}
          />
          <span className={styles['field__radio-text']}>Yes</span>
        </label>
        <label
          htmlFor={noId}
          className={styles['field__radio-option']}
          data-checked={value === false ? 'true' : 'false'}
        >
          <input
            id={noId}
            type="radio"
            name={name}
            value="no"
            checked={value === false}
            onChange={() => onChange(false)}
            className={styles['field__radio-input']}
          />
          <span className={styles['field__radio-text']}>No</span>
        </label>
      </div>

      {field.helpText !== undefined && field.helpText !== '' ? (
        <p className={styles['field__help-text']}>{field.helpText}</p>
      ) : null}

      {showIssues ? (
        <div role="alert">
          {/* role="alert" on a wrapper so the <ul> keeps list semantics
              (axe: 'listitem' rule). */}
          <ul id={errorListId} className={styles['field__error-list']}>
            {issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`}>
                <span
                  aria-hidden="true"
                  className={styles['field__error-icon']}
                >
                  !
                </span>
                {issueMessage(issue)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </fieldset>
  );
}
